<script lang="ts" setup >import { computed as _computed } from 'vue';

import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { AppButton, Heading } from '@pohlcon/design-system';

import { MessageSchema } from '~/locales';
import { matomoAPI } from '~/APIConnection';

import Paragraph from './Paragraph.vue';
import { LegalFooter } from '../../components';
import { useMainStore } from '~/store';
import { useRouter } from 'vue-router';

const { t } = useI18n<{ message: MessageSchema; }>();

const router = useRouter();
const mainStore = useMainStore();

const paragraphs = _computed(() => [
  t('paragraphs.p1.text'),
  t('paragraphs.p2.text'),
  t('paragraphs.p3.text'),
]);

const imagesURLs = [
  './assets/productImages/IP120_Titel.png',
  './assets/productImages/IP120_QQ.png',
  './assets/productImages/IP120_C.png'
];

const handleNextStep = () => {
  mainStore.completeStep(true);
  mainStore.setNextStep();
  router.push(mainStore.currentStep.to);
};

onMounted(() => matomoAPI.generalControllerWelcome());
</script>

<template>
  <div id="home-view">
    <header style="grid-area: title">
      <Heading :level="1" id="greeting" cypress-id="home-view-heading" v-html="t('welcome.headline')" />
      <Heading :level="4" cypress-id="home-view-subtitle" v-html="t('welcome.subtitle')" :type="'dark'"/>
    </header>
    <div style="grid-area: paragraphs" class="paragraphs">
      <Paragraph v-for="(paragraph, key) in paragraphs" :key="key" :paragraph="paragraph" cypress-id="home-view-paragraph" />
      <AppButton
        cypress-id="home-view-button"
        icon="chevron_right"
        :title="t('button.title')"
        :text="t('button.label')"
        :trailingIcon="true"
        secondary
        @click="handleNextStep()"
      />
    </div>
    <div id="home-view__images" style="grid-area: image">
      <img v-for="(URL, i) in imagesURLs" :key="i" :src="URL" :style="{ 'grid-area': `img-${i + 1}` }" loading="lazy"
        alt="" />
    </div>
  </div>
  <LegalFooter>
    <div class="info" v-html="t('footer')" />
  </LegalFooter>
</template>

<style lang="scss" scoped>
@use '@pohlcon/design-system/index.scss' as *;
@use '../../_variables' as *;

#home-view {
  text-align: left;
  display: grid;
  column-gap: var(--gutter-l);
  grid-template:
    "title      image " max-content
    "paragraphs image " 1fr
    "footer     footer" max-content / 3fr max-content;

  &__images {
    max-width: 500px;
    gap: var(--gutter-xs);
    display: grid;
    grid-template:
      "img-1  img-1" max-content
      "img-2  img-3" max-content / 1fr 1fr;

    img {
      width: 100%;
      height: auto;
      object-fit: cover;
    }
  }

  .heading {
    &:first-child {
      margin-bottom: var(--gutter-m);
    }
    &:last-child {
      margin-bottom: var(--gutter-xl);
    }
  }

  .paragraphs {
    @extend %paragraph-light;
    p:first-of-type {
      margin-top: 0px;
    }
    p:last-of-type {
      margin-bottom: var(--gutter-xl);
    }
  }

  @media screen and (max-width: $breakpoint-tablet) {
    grid-template:
      "title      " max-content
      "image      " max-content
      "paragraphs " max-content
      "footer     " max-content / 1fr;

    &__images {
      max-width: none;
      width: 100%;
      grid-template:
        "img-1  img-2" max-content
        "img-1  img-3" max-content / 2fr 1fr;
    }
  }
}
</style>
